import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCenter, useDroppable } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { useAtomValue } from 'jotai';
import { Button, Spinner } from '@nike/podium';
import GenericModal from '~/components/GenericModal';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '~/components/shadcn/sheet';
import LinePlanEntityCard from './LinePlanEntityCard';
import ProductCard from './ProductCard';
import DraggableCard from './DraggableCard';
import { useCardView } from '~/atoms/hooks/useCardView';
import { filteredCardsAtom } from '~/atoms/cardView';
import { useLinePlanEntity } from '~/atoms/hooks/useLinePlanEntity';
import { usePageActions } from '~/atoms/hooks/usePageActions';
import { useGridData } from '~/atoms/hooks/useGridData';
import { toPluralEntity } from '~/utils/linePlanEntity';
import { CaretDown, CaretUp } from '@nike/nike-design-system-icons';
function DroppableGroupSection({ groupId, groupTitle, cards, isExpanded, gridClasses, scale, entityType, onToggleExpand, onGroupMenu, onCardClick }) {
    const { setNodeRef, isOver } = useDroppable({ id: groupId });
    return (_jsxs("div", { className: "border-b border-gray-300 dark:border-gray-600", children: [_jsxs("div", { className: "flex items-center justify-between p-4 border-t border-gray-300 dark:border-gray-600", children: [_jsxs("button", { onClick: onToggleExpand, className: "flex items-center gap-3 flex-1 text-left", children: [_jsx("h2", { className: "text-xl font-bold text-gray-900 dark:text-white", children: groupTitle }), _jsxs("span", { className: "px-2 py-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm", children: [cards.length, " ", cards.length === 1 ? 'item' : 'items'] }), isExpanded ? (_jsx(CaretUp, { className: "w-6 h-6" })) : (_jsx(CaretDown, { className: "w-6 h-6" }))] }), _jsx("button", { onClick: onGroupMenu, className: "p-1 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-600 dark:text-gray-400", "aria-label": "Group options", children: "\u22EE" })] }), isExpanded && (_jsx("div", { ref: setNodeRef, className: `min-h-[200px] ${isOver ? 'border-2 border-blue-500 dark:border-blue-400' : ''}`, children: cards.length > 0 ? (_jsx(SortableContext, { items: cards.map((c) => c.id), strategy: rectSortingStrategy, children: _jsx("div", { className: `${gridClasses} p-6`, children: cards.map((card) => (_jsx(DraggableCard, { card: card, entityType: entityType, onClick: () => onCardClick(card), scale: scale }, card.id))) }) })) : (_jsx("div", { className: `p-12 text-center ${isOver ? 'border-2 border-dashed pds-color-border-accent' : ''}`, children: _jsx("p", { className: "pds-color-text-secondary", children: isOver
                            ? 'Drop card here'
                            : 'No cards in this group. Drag cards here to add them.' }) })) }))] }));
}
function CustomGroupsManager({ open, onClose, groups, onGroupsChange, groupToEdit }) {
    const editingGroup = groupToEdit
        ? groups.find((grp) => grp.id === groupToEdit)
        : null;
    const [groupTitle, setGroupTitle] = useState(editingGroup?.title || '');
    const handleSave = () => {
        if (!groupTitle.trim())
            return;
        if (editingGroup) {
            onGroupsChange(groups.map((grp) => grp.id === editingGroup.id ? { ...grp, title: groupTitle } : grp));
        }
        else {
            onGroupsChange([
                ...groups,
                { id: `group-${Date.now()}`, title: groupTitle, order: groups.length }
            ]);
        }
        setGroupTitle('');
        onClose();
    };
    const handleDelete = () => {
        if (!editingGroup || editingGroup.id === 'ungrouped')
            return;
        onGroupsChange(groups
            .filter((grp) => grp.id !== editingGroup.id)
            .map((grp, idx) => ({ ...grp, order: idx })));
        onClose();
    };
    const canDelete = editingGroup && editingGroup.id !== 'ungrouped';
    return (_jsx(GenericModal, { open: open, onOpenChange: () => {
            setGroupTitle('');
            onClose();
        }, title: editingGroup ? 'Edit Group' : 'Add Custom Group', children: _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { children: [_jsx("label", { htmlFor: "group-title", className: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2", children: "Group Name" }), _jsx("input", { id: "group-title", type: "text", value: groupTitle, onChange: (e) => setGroupTitle(e.target.value), placeholder: "e.g., My Favorites, Q4 Launch", className: "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" })] }), _jsxs("div", { className: "flex gap-2 justify-end pt-4", children: [canDelete && (_jsx(Button, { variant: "outlined", onClick: handleDelete, size: "m", children: "Delete" })), _jsx(Button, { variant: "outlined", onClick: () => {
                                setGroupTitle('');
                                onClose();
                            }, size: "m", children: "Cancel" }), _jsx(Button, { variant: "filled", onClick: handleSave, size: "m", disabled: !groupTitle.trim(), children: editingGroup ? 'Save' : 'Add' })] })] }) }));
}
function GroupOrderManager({ open, onOpenChange, groups, onGroupsChange }) {
    const [searchTerm, setSearchTerm] = useState('');
    const filteredGroups = useMemo(() => {
        const sorted = groups.sort((a, b) => a.order - b.order);
        return searchTerm.trim()
            ? sorted.filter((group) => group.title.toLowerCase().includes(searchTerm.toLowerCase()))
            : sorted;
    }, [groups, searchTerm]);
    const moveGroup = (index, direction) => {
        const sortedGroups = [...groups].sort((a, b) => a.order - b.order);
        if ((direction === 'up' && index === 0) ||
            (direction === 'down' && index === sortedGroups.length - 1))
            return;
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        const temp = sortedGroups[targetIndex].order;
        sortedGroups[targetIndex].order = sortedGroups[index].order;
        sortedGroups[index].order = temp;
        onGroupsChange(sortedGroups);
    };
    return (_jsx(Sheet, { open: open, onOpenChange: onOpenChange, children: _jsxs(SheetContent, { side: "right", className: "w-full sm:max-w-lg overflow-y-auto p-8 m-4 h-[calc(100vh-2rem)] rounded-none", children: [_jsx(SheetHeader, { className: "px-0", children: _jsx(SheetTitle, { className: "text-2xl font-bold", children: "Manage Group Order" }) }), _jsxs("div", { className: "mt-8 space-y-6", children: [_jsxs("div", { children: [_jsx("label", { htmlFor: "group-search", className: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2", children: "Search Groups" }), _jsx("input", { id: "group-search", type: "text", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), placeholder: "Search by group name...", className: "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" })] }), _jsxs("div", { children: [_jsxs("h3", { className: "text-lg font-semibold mb-4 text-gray-900 dark:text-white", children: ["Group Order", ' ', searchTerm && `(${filteredGroups.length} of ${groups.length})`] }), _jsx("div", { className: "space-y-2 max-h-96 overflow-y-auto", children: filteredGroups.map((group) => {
                                        const actualIndex = groups
                                            .sort((a, b) => a.order - b.order)
                                            .findIndex((g) => g.id === group.id);
                                        return (_jsxs("div", { className: "flex items-center justify-between p-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900", children: [_jsxs("div", { className: "flex items-center gap-3 flex-1", children: [_jsx("span", { className: "text-gray-400 dark:text-gray-500 cursor-move", children: "\u22EE\u22EE" }), _jsx("span", { className: "text-sm font-medium pds-color-text-primary", children: group.title }), group.id === 'ungrouped' && (_jsx("span", { className: "text-xs pds-color-text-secondary", children: "(Default)" }))] }), _jsxs("div", { className: "flex gap-2", children: [_jsx("button", { onClick: () => moveGroup(actualIndex, 'up'), disabled: actualIndex === 0 || searchTerm !== '', className: "px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover\tbg-gray-800 transition-colors", "aria-label": "Move up", title: searchTerm ? 'Clear search to reorder' : 'Move up', children: "\u2191" }), _jsx("button", { onClick: () => moveGroup(actualIndex, 'down'), disabled: actualIndex === groups.length - 1 || searchTerm !== '', className: "px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors", "aria-label": "Move down", title: searchTerm ? 'Clear search to reorder' : 'Move down', children: "\u2193" })] })] }, group.id));
                                    }) })] }), searchTerm && (_jsx("div", { className: "pt-4 border-t border-gray-300 dark:border-gray-600", children: _jsx(Button, { variant: "outlined", onClick: () => setSearchTerm(''), size: "m", className: "w-full", children: "Clear Search" }) }))] })] }) }));
}
function CardViewWithCustomGroups() {
    const { customGroups, setCustomGroups, cardGroupAssignments, setCardGroupAssignments, cardOrder, setCardOrder, cardScale, customGroupsManagerOpen, setCustomGroupsManagerOpen, groupOrderManagerOpen, setGroupOrderManagerOpen } = useCardView();
    const { linePlanEntity } = useLinePlanEntity();
    const { setDetailSidebar } = usePageActions();
    const { gridData, gridDataLoadingState } = useGridData();
    const filteredCards = useAtomValue(filteredCardsAtom);
    const [activeCard, setActiveCard] = useState(null);
    const groupManagerOpen = customGroupsManagerOpen;
    const [editingGroupId, setEditingGroupId] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState(new Set(customGroups.map((g) => g.id)));
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: {
            distance: 8
        }
    }));
    const cardsByGroup = useMemo(() => {
        const byGroup = {};
        customGroups.forEach((group) => {
            byGroup[group.id] = [];
        });
        filteredCards.forEach((card) => {
            const groupId = cardGroupAssignments[card.id] || 'ungrouped';
            if (byGroup[groupId]) {
                byGroup[groupId].push(card);
            }
            else {
                if (!byGroup['ungrouped']) {
                    byGroup['ungrouped'] = [];
                }
                byGroup['ungrouped'].push(card);
            }
        });
        Object.keys(byGroup).forEach((groupId) => {
            const order = cardOrder[groupId] || [];
            byGroup[groupId] = byGroup[groupId].sort((a, b) => {
                const indexA = order.indexOf(a.id);
                const indexB = order.indexOf(b.id);
                if (indexA === -1 && indexB === -1)
                    return 0;
                if (indexA === -1)
                    return 1;
                if (indexB === -1)
                    return -1;
                return indexA - indexB;
            });
        });
        return byGroup;
    }, [filteredCards, cardGroupAssignments, customGroups, cardOrder]);
    const getGridClasses = () => {
        const baseClasses = 'grid grid-cols-1';
        switch (cardScale) {
            case 'small':
                return `${baseClasses} sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-4`;
            case 'large':
                return `${baseClasses} sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8`;
            case 'medium':
            default:
                return `${baseClasses} sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6`;
        }
    };
    const findCardGroup = (cardId) => {
        return Object.keys(cardsByGroup).find((grpId) => cardsByGroup[grpId].some((card) => card.id === cardId));
    };
    const handleDragStart = (event) => {
        const card = filteredCards.find((c) => c.id === event.active.id);
        if (card) {
            setActiveCard(card);
        }
    };
    const handleDragOver = (event) => {
        const { active, over } = event;
        if (!over)
            return;
        const activeCardId = active.id;
        const overId = over.id;
        const currentGroup = findCardGroup(activeCardId);
        const isOverGroup = customGroups.some((grp) => grp.id === overId);
        if (isOverGroup && currentGroup !== overId) {
            setCardGroupAssignments({
                ...cardGroupAssignments,
                [activeCardId]: overId
            });
            return;
        }
        const overGroupId = findCardGroup(overId);
        if (overGroupId && currentGroup !== overGroupId) {
            setCardGroupAssignments({
                ...cardGroupAssignments,
                [activeCardId]: overGroupId
            });
        }
    };
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            setActiveCard(null);
            return;
        }
        const activeCardId = active.id;
        const overId = over.id;
        const activeGroupId = findCardGroup(activeCardId);
        const isOverGroup = customGroups.some((grp) => grp.id === overId);
        if (isOverGroup) {
            const targetGroupCards = cardsByGroup[overId] || [];
            const newOrder = [
                ...targetGroupCards
                    .filter((c) => c.id !== activeCardId)
                    .map((c) => c.id),
                activeCardId
            ];
            setCardOrder({
                ...cardOrder,
                [overId]: newOrder
            });
            setActiveCard(null);
            return;
        }
        const overGroupId = findCardGroup(overId);
        if (activeGroupId === overGroupId && activeGroupId) {
            // Reordering within the same group
            const groupCards = cardsByGroup[activeGroupId];
            const oldIndex = groupCards.findIndex((card) => card.id === activeCardId);
            const newIndex = groupCards.findIndex((card) => card.id === overId);
            if (oldIndex !== newIndex) {
                const newOrder = [...groupCards];
                const [removed] = newOrder.splice(oldIndex, 1);
                newOrder.splice(newIndex, 0, removed);
                setCardOrder({
                    ...cardOrder,
                    [activeGroupId]: newOrder.map((card) => card.id)
                });
            }
        }
        else if (overGroupId) {
            // Dropped on a card in a different group
            const targetGroupCards = cardsByGroup[overGroupId] || [];
            const overIndex = targetGroupCards.findIndex((card) => card.id === overId);
            const newOrder = [
                ...targetGroupCards
                    .filter((c) => c.id !== activeCardId)
                    .map((c) => c.id)
            ];
            newOrder.splice(overIndex, 0, activeCardId);
            setCardOrder({
                ...cardOrder,
                [overGroupId]: newOrder
            });
        }
        setActiveCard(null);
    };
    const handleCardClick = (card) => {
        if (!gridData)
            return;
        const originalEntity = gridData.find((item) => {
            if ('modelId' in item &&
                typeof item.modelId === 'number' &&
                !('styleNumber' in item)) {
                return String(item.modelId) === card.id;
            }
            if ('styleNumber' in item && typeof item.styleNumber === 'string') {
                return item.styleNumber === card.id;
            }
            if ('projectName' in item &&
                'id' in item &&
                typeof item.id === 'string') {
                return item.id === card.id;
            }
            if ('expressionName' in item &&
                'id' in item &&
                typeof item.id === 'string') {
                return item.id === card.id;
            }
            return false;
        });
        if (originalEntity) {
            setDetailSidebar({
                isOpen: true,
                entity: originalEntity,
                entityType: linePlanEntity
            });
        }
    };
    const handleGroupMenu = (groupId) => {
        setEditingGroupId(groupId);
        setCustomGroupsManagerOpen(true);
    };
    const handleGroupManagerClose = () => {
        setCustomGroupsManagerOpen(false);
        setEditingGroupId(null);
    };
    const toggleGroup = (groupId) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) {
                next.delete(groupId);
            }
            else {
                next.add(groupId);
            }
            return next;
        });
    };
    if (gridDataLoadingState === 'loading') {
        return (_jsx("div", { className: "flex items-center justify-center h-96", children: _jsx(Spinner, { size: "large" }) }));
    }
    if (gridDataLoadingState === 'hasError') {
        return (_jsx("div", { className: "flex items-center justify-center h-96", children: _jsxs("div", { className: "text-center", children: [_jsxs("p", { className: "text-xl pds-color-text-secondary mb-2", children: ["Failed to load ", toPluralEntity(linePlanEntity)] }), _jsx("p", { className: "text-sm pds-color-text-secondary", children: "Please try refreshing the page" })] }) }));
    }
    if (gridDataLoadingState === 'hasData' && filteredCards.length === 0) {
        return (_jsx("div", { className: "flex items-center justify-center h-96", children: _jsxs("div", { className: "text-center", children: [_jsx("p", { className: "text-xl pds-color-text-secondary mb-2", children: `No ${toPluralEntity(linePlanEntity)} found` }), _jsx("p", { className: "text-sm pds-color-text-secondary", children: "Try adjusting your filters or search term" })] }) }));
    }
    return (_jsxs(_Fragment, { children: [_jsxs(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragStart: handleDragStart, onDragOver: handleDragOver, onDragEnd: handleDragEnd, children: [_jsx("div", { className: "p-6 space-y-6", children: customGroups
                            .sort((a, b) => a.order - b.order)
                            .map((group) => {
                            const cards = cardsByGroup[group.id] || [];
                            const isExpanded = expandedGroups.has(group.id);
                            return (_jsx(DroppableGroupSection, { groupId: group.id, groupTitle: group.title, cards: cards, isExpanded: isExpanded, gridClasses: getGridClasses(), scale: cardScale, entityType: linePlanEntity, onToggleExpand: () => toggleGroup(group.id), onGroupMenu: () => handleGroupMenu(group.id), onCardClick: handleCardClick }, group.id));
                        }) }), _jsx(DragOverlay, { children: activeCard ? (linePlanEntity === 'project' || linePlanEntity === 'expression' ? (_jsx(LinePlanEntityCard, { card: activeCard, entityType: linePlanEntity, scale: cardScale })) : (_jsx(ProductCard, { card: activeCard, scale: cardScale }))) : null })] }), _jsx(CustomGroupsManager, { open: groupManagerOpen, onClose: handleGroupManagerClose, groups: customGroups, onGroupsChange: setCustomGroups, groupToEdit: editingGroupId }), _jsx(GroupOrderManager, { open: groupOrderManagerOpen, onOpenChange: setGroupOrderManagerOpen, groups: customGroups, onGroupsChange: setCustomGroups })] }));
}
export default CardViewWithCustomGroups;
